(() => {
  const TAG = 'teaballs-mix-dock';
  if (customElements.get(TAG)) return;

  const pathOf = (value) => {
    if (!value) return '';
    try { return new URL(value, window.location.origin).pathname.replace(/\/$/, ''); }
    catch { return String(value).split('?')[0].replace(/\/$/, ''); }
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[char]);

  class TeaballsMixDock extends HTMLElement {
    constructor() {
      super();
      this.abort = null;
      this.unsubscribe = null;
      this.cart = null;
      this.refreshTimer = 0;
      this.busy = false;
    }

    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.openButton = this.querySelector('[data-dock-open]');
      this.dialog = this.querySelector('[data-dock-dialog]');
      this.closeButton = this.querySelector('[data-dock-close]');
      this.cartButton = this.querySelector('[data-dock-cart]');

      this.openButton?.addEventListener('click', () => this.open(), { signal });
      this.closeButton?.addEventListener('click', () => this.close(), { signal });
      this.cartButton?.addEventListener('click', () => this.openCart(), { signal });
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      }, { signal });

      document.addEventListener('teaballs:promotion:ready', () => this.bindPromotion(), { signal });
      document.addEventListener('teaballs:promotion:change', () => this.render(), { signal });
      document.addEventListener('teaballs:promotion:tick', () => this.renderCountdown(), { signal });
      document.addEventListener('shopify:cart:lines-update', (event) => this.onCartEvent(event), { signal });
      ['cart:updated', 'cart:change', 'cart:refresh'].forEach((name) => {
        document.addEventListener(name, () => this.scheduleRefresh(80), { signal });
      });
      window.addEventListener('pageshow', () => this.scheduleRefresh(0), { signal });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.scheduleRefresh(0);
      }, { signal });

      if (window.TeaballsPromotion) this.bindPromotion();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.unsubscribe?.();
      window.clearTimeout(this.refreshTimer);
    }

    labels() {
      return {
        title: this.dataset.labelTitle || '',
        start: this.dataset.labelStart || '',
        bottleOne: this.dataset.labelBottleOne || '',
        bottleMany: this.dataset.labelBottleMany || '',
        until: this.dataset.labelUntil || '',
        active: this.dataset.labelActive || '',
        saved: this.dataset.labelSaved || '',
        empty: this.dataset.labelEmpty || '',
        shippingLeft: this.dataset.labelShippingLeft || '',
        shippingReached: this.dataset.labelShippingReached || '',
        shippingActive: this.dataset.labelShippingActive || '',
        giftUnlocked: this.dataset.labelGiftUnlocked || '',
        giftLeft: this.dataset.labelGiftLeft || '',
        quantity: this.dataset.labelQuantity || '',
      };
    }

    bindPromotion() {
      this.unsubscribe?.();
      const promo = window.TeaballsPromotion;
      if (!promo) return;
      this.unsubscribe = promo.subscribe(() => this.render());
      this.render();
      this.refreshCart();
    }

    configuredCollection() {
      const promo = window.TeaballsPromotion;
      if (!promo) return false;
      const raw = promo.raw();
      const currentPath = pathOf(this.dataset.collectionUrl || window.location.pathname);
      const urls = [raw?.mix?.allCollectionUrl, raw?.mix?.classicCollectionUrl, raw?.mix?.bioCollectionUrl]
        .map(pathOf)
        .filter(Boolean);
      return urls.includes(currentPath);
    }

    active() {
      const promo = window.TeaballsPromotion;
      return Boolean(promo?.surfaceEnabled('mixDock') && this.configuredCollection());
    }

    onCartEvent(event) {
      const promise = event?.promise;
      if (promise && typeof promise.then === 'function') {
        promise.finally(() => this.scheduleRefresh(40));
      } else {
        this.scheduleRefresh(80);
      }
    }

    scheduleRefresh(delay = 60) {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refreshCart(), delay);
    }

    async refreshCart() {
      if (this.busy) return;
      this.busy = true;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (!response.ok) throw new Error(String(response.status));
        this.cart = await response.json();
      } catch {
        this.cart = null;
      } finally {
        this.busy = false;
        this.render();
      }
    }

    bottleWord(quantity) {
      const l = this.labels();
      return quantity === 1 ? l.bottleOne : l.bottleMany;
    }

    actualEligibleSavings(state) {
      const ids = new Set((state?.eligibleCollection?.productIds || []).map(String));
      return (this.cart?.items || []).reduce((sum, line) => {
        if (!ids.has(String(line.product_id))) return sum;
        const original = Number(line.original_line_price ?? line.line_price ?? line.final_line_price ?? 0);
        const finalPrice = Number(line.final_line_price ?? original);
        return sum + Math.max(0, original - finalPrice);
      }, 0);
    }

    eligibleLines(state) {
      const ids = new Set((state?.eligibleCollection?.productIds || []).map(String));
      return (this.cart?.items || []).filter((line) => ids.has(String(line.product_id)));
    }

    primaryMessage(state, metrics) {
      const l = this.labels();
      const offer = state?.offer || {};
      const quantity = metrics?.eligibleQuantity || 0;

      if (state.copy?.mixDock) return state.copy.mixDock;

      if (offer.type === 'tiered') {
        const progress = metrics?.tier;
        if (!progress?.current && progress?.next) {
          return `${progress.remaining} ${this.bottleWord(progress.remaining)} ${l.until} ${progress.next.discountPercent}%`;
        }
        if (progress?.current && progress?.next) {
          return `${progress.current.discountPercent}% ${l.active} · ${progress.remaining} ${this.bottleWord(progress.remaining)} ${l.until} ${progress.next.discountPercent}%`;
        }
        if (progress?.current) return `${progress.current.discountPercent}% ${l.active}`;
        return l.start;
      }

      if (offer.thresholdType === 'quantity' && quantity < Number(offer.thresholdQuantity || 0)) {
        const remaining = Math.max(0, Number(offer.thresholdQuantity || 0) - quantity);
        return `${remaining} ${this.bottleWord(remaining)} ${l.until} ${state.publicName || l.title}`;
      }
      if (offer.thresholdType === 'spend' && metrics.eligibleSubtotalCents < Number(offer.thresholdSpendCents || 0)) {
        const remaining = Math.max(0, Number(offer.thresholdSpendCents || 0) - metrics.eligibleSubtotalCents);
        return `${window.TeaballsPromotion.formatMoney(remaining)} ${l.until} ${state.publicName || l.title}`;
      }
      if (offer.type === 'flat_percent') return `${state.publicName || l.title} · ${offer.flatPercent}% ${l.active}`;
      if (offer.type === 'fixed_amount') return `${state.publicName || l.title} · ${window.TeaballsPromotion.formatMoney(offer.fixedAmountCents)} ${l.active}`;
      if (offer.type === 'bxgy') return state.publicName || l.title;
      return state.publicName || l.start;
    }

    progressData(state, metrics) {
      const quantity = metrics?.eligibleQuantity || 0;
      if (state?.offer?.type === 'tiered') {
        const current = metrics?.tier?.current?.minQuantity || 0;
        const next = metrics?.tier?.next?.minQuantity || current || 1;
        if (!metrics?.tier?.next) return { percent: quantity ? 100 : 0, label: this.primaryMessage(state, metrics), value: `${quantity}` };
        const span = Math.max(1, next - current);
        const percent = Math.max(0, Math.min(100, ((quantity - current) / span) * 100));
        return { percent, label: this.primaryMessage(state, metrics), value: `${quantity}/${next}` };
      }
      if (metrics?.goal?.next) {
        const current = metrics.goal.current?.quantity || 0;
        const next = metrics.goal.next.quantity;
        const span = Math.max(1, next - current);
        const percent = Math.max(0, Math.min(100, ((quantity - current) / span) * 100));
        return { percent, label: metrics.goal.next.label || this.primaryMessage(state, metrics), value: `${quantity}/${next}` };
      }
      return { percent: quantity ? 100 : 0, label: this.primaryMessage(state, metrics), value: `${quantity}` };
    }

    render() {
      const promo = window.TeaballsPromotion;
      const state = promo?.current();
      const editorNote = this.querySelector('[data-dock-editor-note]');

      if (!state || !this.active()) {
        this.hidden = true;
        if (editorNote && window.Shopify?.designMode) {
          editorNote.hidden = false;
          this.hidden = false;
          this.openButton.hidden = true;
        }
        return;
      }

      this.hidden = false;
      if (editorNote) editorNote.hidden = true;
      this.openButton.hidden = false;

      const metrics = promo.metricsFromCart(this.cart || { items: [] });
      const l = this.labels();
      const quantity = metrics.eligibleQuantity || 0;
      const savings = this.actualEligibleSavings(state);
      const bmActive=(document.documentElement.dataset.tbIdentitySurfaces||'').split(' ').includes('cart');
      const primary = bmActive ? this.dataset.bmTitle : this.primaryMessage(state, metrics);
      const secondaryParts = [`${quantity} ${this.bottleWord(quantity)}`];
      if (savings > 0) secondaryParts.push(`${promo.formatMoney(savings)} ${l.saved}`);

      this.querySelector('[data-dock-eyebrow]').textContent = state.publicName || l.title;
      this.querySelector('[data-dock-primary]').textContent = primary;
      this.querySelector('[data-dock-secondary]').textContent = secondaryParts.join(' · ');

      const progress = this.progressData(state, metrics);
      this.querySelector('[data-dock-meter]').style.width = `${progress.percent}%`;
      this.querySelector('[data-dialog-meter]').style.width = `${progress.percent}%`;
      this.querySelector('[data-dialog-progress-label]').textContent = progress.label;
      this.querySelector('[data-dialog-progress-value]').textContent = progress.value;
      this.querySelector('[data-dialog-eyebrow]').textContent = state.publicName || l.title;
      this.querySelector('[data-dialog-title]').textContent = primary;
      this.querySelector('[data-dialog-status]').textContent = secondaryParts.join(' · ');
      this.querySelector('[data-dock-count]').textContent = `${quantity} ${this.bottleWord(quantity)}`;
      this.querySelector('[data-dock-savings]').textContent = savings > 0 ? `${promo.formatMoney(savings)} ${l.saved}` : '';

      this.renderNavigation(promo.raw());
      this.renderLines(state);
      this.renderGift(metrics, promo);
      this.renderShipping(state, metrics, promo);
      this.renderCountdown();
    }

    renderNavigation(raw) {
      const current = pathOf(this.dataset.collectionUrl || window.location.pathname);
      const links = [
        ['[data-nav-all]', raw?.mix?.allCollectionUrl],
        ['[data-nav-classic]', raw?.mix?.classicCollectionUrl],
        ['[data-nav-bio]', raw?.mix?.bioCollectionUrl],
      ];
      for (const [selector, url] of links) {
        const link = this.querySelector(selector);
        const path = pathOf(url);
        link.hidden = !path;
        if (!path) continue;
        link.href = url;
        if (path === current) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      }
      this.querySelector('[data-dock-nav]').hidden = links.every(([selector]) => this.querySelector(selector).hidden);
    }

    renderLines(state) {
      const lines = this.eligibleLines(state);
      const container = this.querySelector('[data-dock-lines]');
      const empty = this.querySelector('[data-dock-empty]');
      empty.hidden = lines.length > 0;
      container.innerHTML = lines.map((line) => {
        const image = line.featured_image?.url || line.image || '';
        const quantity = Number(line.quantity) || 0;
        const finalPrice = Number(line.final_line_price ?? line.line_price ?? 0);
        const title = line.product_title || line.title || '';
        return `<div class="tb-mix-dock__line">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : '<span></span>'}<div class="tb-mix-dock__line-copy"><strong>${escapeHtml(title)}</strong><span>${escapeHtml(this.labels().quantity)}: ${quantity}</span></div><div class="tb-mix-dock__line-price">${escapeHtml(window.TeaballsPromotion.formatMoney(finalPrice))}</div></div>`;
      }).join('');
    }

    renderGift(metrics, promo) {
      const node = this.querySelector('[data-dock-gift]');
      const gift = metrics?.gifts?.[0];
      if (!gift) { node.hidden = true; return; }
      const l = this.labels();
      node.hidden = false;
      if (gift.qualified) {
        node.textContent = `${l.giftUnlocked}${gift.label ? ` · ${gift.label}` : ''}`;
      } else if (gift.triggerType === 'cart_subtotal') {
        node.textContent = `${promo.formatMoney(gift.remaining)} ${l.giftLeft}${gift.label ? ` · ${gift.label}` : ''}`;
      } else {
        node.textContent = `${gift.remaining} ${this.bottleWord(gift.remaining)} ${l.giftLeft}${gift.label ? ` · ${gift.label}` : ''}`;
      }
    }

    renderShipping(state, metrics, promo) {
      const node = this.querySelector('[data-dock-shipping]');
      const l = this.labels();
      if (state?.shipping?.mode === 'free') {
        node.hidden = false;
        node.textContent = l.shippingActive;
        return;
      }
      if (state?.shipping?.mode !== 'threshold' || !state.shipping.thresholdCents) {
        node.hidden = true;
        return;
      }
      node.hidden = false;
      node.textContent = metrics.shippingRemainingCents > 0
        ? `${promo.formatMoney(metrics.shippingRemainingCents)} ${l.shippingLeft}`
        : l.shippingReached;
    }

    renderCountdown() {
      const promo = window.TeaballsPromotion;
      const node = this.querySelector('[data-dock-countdown]');
      const countdown = promo?.countdown();
      if (!countdown?.visible) { node.hidden = true; return; }
      node.hidden = false;
      node.textContent = `${countdown.label ? `${countdown.label} ` : ''}${countdown.text}`;
    }

    open() {
      if (!this.dialog || !this.active()) return;
      this.openButton?.setAttribute('aria-expanded', 'true');
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      this.refreshCart();
    }

    close() {
      this.openButton?.setAttribute('aria-expanded', 'false');
      if (typeof this.dialog?.close === 'function') this.dialog.close();
      else this.dialog?.removeAttribute('open');
      this.openButton?.focus({ preventScroll: true });
    }

    async openCart() {
      this.close();
      if (window.Shopify?.actions?.openCart) {
        try { await window.Shopify.actions.openCart(); return; } catch {}
      }
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer?.open) {
        drawer.open();
        return;
      }
      window.location.assign(this.dataset.cartUrl || '/cart');
    }
  }

  customElements.define(TAG, TeaballsMixDock);
})();
